import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spinner, SpinnerSize, MessageBar, MessageBarType } from '@fluentui/react';
import { useDynamicReducer } from '@micro-frontend-react/employee-experience/lib/useDynamicReducer';
import {
  fetchEmailTemplates,
  fetchTemplateVariables,
  clearSaveStatus,
} from '../../Redux/EmailTemplates/actions';
import { emailTemplatesReducer } from '../../store/emailTemplates/emailTemplatesReducer';
import { emailTemplatesSaga } from '../../store/emailTemplates/emailTemplatesSaga';
import { emailTemplatesReducerName, selectEmailTemplatesState } from './emailTemplatesState';
import { usePermission, useFeatureTreeLoading } from '../../Hooks/useFeature';
import { EmailTemplateList } from './EmailTemplateList';
import { EmailTemplateEditPanel } from './EmailTemplateEditPanel';
import { styles } from './EmailTemplateAdmin.styled';

export const EmailTemplateAdmin: React.FC = () => {
  useDynamicReducer(emailTemplatesReducerName, emailTemplatesReducer, [emailTemplatesSaga]);
  const dispatch = useDispatch();
  const { loading, error, correlationId, saveSuccess, selectedTemplate } = useSelector(
    selectEmailTemplatesState
  );
  // Admin-only surface: gate on the same permission the API and nav/route enforce
  // (Email.ManageTemplates is seeded to the Admin role only).
  const rolesLoading = useFeatureTreeLoading();
  const isAdmin = usePermission('Email.ManageTemplates');
  const [successVisible, setSuccessVisible] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    dispatch(fetchEmailTemplates());
    dispatch(fetchTemplateVariables());
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (saveSuccess) {
      setSuccessVisible(true);
      const timer = setTimeout(() => {
        setSuccessVisible(false);
        dispatch(clearSaveStatus());
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess, dispatch]);

  if (rolesLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size={SpinnerSize.large} label="Checking permissions..." ariaLabel="Checking permissions" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className={styles.accessDenied} role="alert">
        <span className={styles.accessDeniedTitle}>Access Denied</span>
        <span className={styles.accessDeniedText}>
          You do not have permission to access the Email Template Admin.
          Please contact your administrator for access.
        </span>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Email Templates</h1>
        <p className={styles.pageSubtitle}>
          Manage PBT email templates for the termination workflow
        </p>
      </div>

      <div className={styles.contentArea}>
        {loading && (
          <div className={styles.loadingContainer}>
            <Spinner size={SpinnerSize.large} label="Loading templates..." ariaLabel="Loading email templates" />
          </div>
        )}

        {!loading && error && !selectedTemplate && (
          <div style={{ padding: '16px 24px', width: '100%' }}>
            <MessageBar
              messageBarType={MessageBarType.error}
              isMultiline
              dismissButtonAriaLabel="Dismiss error"
            >
              {error}
              {correlationId && (
                <span style={{ marginLeft: '8px', fontSize: '11px', color: '#a19f9d' }}>
                  Correlation ID: {correlationId}
                </span>
              )}
            </MessageBar>
          </div>
        )}

        {successVisible && (
          <MessageBar
            messageBarType={MessageBarType.success}
            onDismiss={() => {
              setSuccessVisible(false);
              dispatch(clearSaveStatus());
            }}
            dismissButtonAriaLabel="Dismiss success message"
            styles={{
              root: {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 100,
              },
            }}
          >
            Template saved successfully.
          </MessageBar>
        )}

        {!loading && <EmailTemplateList />}

        {selectedTemplate && <EmailTemplateEditPanel />}
      </div>
    </div>
  );
};
